/**
 * What a platform changes for a member, and nothing more (`MEM-043` AC-7).
 *
 * ## Why this file is so short
 *
 * AC-7 is two clauses and the second one is the instruction: *"Supported platforms follow the
 * `SYS` matrix; **this story states only what changes member behaviour**."* The matrix — which
 * browsers and operating systems are supported, at which versions, and what the product does on
 * an unsupported one — is `SYS-030`'s, it has not been intaken, and `MEM-043` puts it explicitly
 * out of scope. `PLT-008` is the story that will carry it (`depends_on: [MEM-043]`, sprint 5).
 *
 * So this repository does not get a support matrix today, and writing one here would be worse
 * than not having one: it would be a second, unowned copy that `SYS` intake then contradicts.
 *
 * What *does* belong here is the handful of platform facts a member can feel, because each one
 * changes what a screen must say. They are derived from capabilities rather than from user-agent
 * strings — a member on a browser this file has never heard of gets the answer their browser
 * actually supports.
 *
 * ## The three that matter today
 *
 *   1. **iOS installs by hand.** Safari fires no `beforeinstallprompt`; there is no dialogue to
 *      raise. The member has to use Share → Add to Home Screen, so the install control on iOS is
 *      an instruction rather than a button (`install-app-card.tsx`).
 *   2. **On iOS, push requires the installed app.** Safari only grants web push to a page added
 *      to the home screen. Offering push in the browser there would be offering something that
 *      cannot be granted, so the offer withholds itself — which is `MEM-040` AC-5 again: the
 *      member loses push and loses nothing else. `PLT-008` AC-6 states the same fact.
 *   3. **Scanning needs a phone.** Already built and already honest —
 *      `domains/member-in-event/constants.ts`'s `canRunScanner` gates on a camera *and* a
 *      touch-primary pointer, and the desktop gets an explanation rather than a dead control
 *      (`MEM-043` AC-5, `MEM-031` AC-1). Named here so the three live in one list; owned there.
 */

/** How the current browser offers installation. Capability-derived; see the header. */
export type InstallMechanism =
  /** The browser fires `beforeinstallprompt` and the app can raise a real dialogue. */
  | 'prompt'
  /** Safari on iOS: Share → Add to Home Screen, by hand. */
  | 'manual-ios'
  /** Already installed, or a browser with no home-screen concept. Nothing to offer. */
  | 'none';

/**
 * Whether this is iOS or iPadOS.
 *
 * **The one user-agent test in this slice, and it is here under protest.** There is no capability
 * that distinguishes "Safari will never fire `beforeinstallprompt`" from "Chromium has not fired
 * it yet", and the two need different words on the screen: an instruction in the first case, and
 * silence in the second. The alternative is showing iOS's manual instructions to everybody whose
 * event has not arrived, which is most Chromium members for the first few seconds.
 *
 * iPadOS reports itself as a Mac, so the touch-point check is what catches it.
 */
export function isAppleTouchPlatform(candidate: {
  readonly userAgent?: string;
  readonly platform?: string;
  readonly maxTouchPoints?: number;
}): boolean {
  const agent = candidate.userAgent ?? '';
  if (/iPhone|iPod/u.test(agent)) return true;
  if (/iPad/u.test(agent)) return true;
  // iPadOS 13+ claims to be macOS; a Mac with a touchscreen does not exist.
  const isMac = /Macintosh/u.test(agent) || candidate.platform === 'MacIntel';
  return isMac && (candidate.maxTouchPoints ?? 0) > 1;
}

/** Which of the three install stories this browser is in. */
export function installMechanism(input: {
  readonly canPrompt: boolean;
  readonly alreadyInstalled: boolean;
  readonly appleTouch: boolean;
}): InstallMechanism {
  if (input.alreadyInstalled) return 'none';
  if (input.canPrompt) return 'prompt';
  if (input.appleTouch) return 'manual-ios';
  return 'none';
}

/**
 * Whether web push can be granted at all on this platform right now (`PLT-008` AC-6).
 *
 * On iOS the answer is no until the app is on the home screen, whatever the `PushManager` object
 * suggests. Everywhere else the capability check in `push-registration.ts` is the whole answer.
 */
export function pushCanBeGranted(input: {
  readonly supported: boolean;
  readonly appleTouch: boolean;
  readonly alreadyInstalled: boolean;
}): boolean {
  if (!input.supported) return false;
  if (input.appleTouch && !input.alreadyInstalled) return false;
  return true;
}

/**
 * Whether push is unavailable **for the one reason a member can do something about**
 * (`PLT-008` AC-6).
 *
 * The distinction this draws is the whole of AC-6. `pushCanBeGranted` returning false covers
 * every reason there is — a browser with no `PushManager`, a deployment with no key, an iOS tab —
 * and most of them are nothing to tell a member about, because there is no action behind them.
 * *This* one has an action: add the app to the home screen and push starts working.
 *
 * A member who turns notifications on in an iOS browser tab and then hears nothing concludes the
 * product is broken. Since `MEM-040` correctly withholds the offer there, the alternative to
 * saying this is saying nothing at all — which is the state this repository was in.
 */
export function pushBlockedByIosInstall(input: {
  /** The browser has the push APIs at all. Capability, before the platform rule is applied. */
  readonly capable: boolean;
  readonly appleTouch: boolean;
  readonly alreadyInstalled: boolean;
}): boolean {
  return input.capable && input.appleTouch && !input.alreadyInstalled;
}

/**
 * The iOS push consequence, in the member's words, stated once (`PLT-008` AC-6).
 *
 * Shown in every place it applies — the install card on My Account, the install offer after a
 * registration, and the notifications surface where a member goes looking for push and finds it
 * withheld. One constant rather than three sentences, because three sentences drift and the one
 * that drifts is the one nobody reads twice.
 */
export const IOS_PUSH_NEEDS_INSTALL =
  'Notifications are only available on iPhone and iPad once the app is on your home screen.';
